import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, type Theme, Typography } from '@mui/material';
import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedCount } from '@/components/AnimatedCount';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useSavePortfolioBridge } from '@/hooks/useSavePortfolioBridge';
import { usePortfolioBridgeStore } from '@/pages/OilAndGasMap/PortfolioBridge/portfolioBridgeStore';
import { MaAssetEntityTypeEnum } from '@/types/GraphqlSchema';
import { sxVariants } from '@/utils/combineSx';
import { PORTFOLIO_ANALYSIS_PATH } from '@/utils/constants';

const portfolioBridgePanelStyles = {
  panel: {
    position: 'absolute',
    top: '5rem',
    left: '50%',
    translate: '-50% 0 0',
    opacity: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: 'clamp(15rem, 40vw, 30rem)',
    '--blur': 8,
    '--saturate': 2.8,
    '--brightness': 1.25,
    '--contrast': 1,
    '--border': 4,
    '--padding': '0.75rem',
    '--border-radius': '0.5rem',
    '--shadow-color': 'oklch(0 0 0 / 0.07)',
    '--bg-color-first': 'oklch(0.98 0 0)',
    '--bg-color-second': 'oklch(0.23 0 0)',
    '--text-color': 'oklch(0.26 0 0 / 0.95)',
    color: 'var(--text-color)',
    boxShadow: `
      0 1px 2px var(--shadow-color),
      0 2px 4px var(--shadow-color),
      0 4px 8px var(--shadow-color),
      0 8px 16px var(--shadow-color),
      0 16px 32px var(--shadow-color),
      0 32px 64px var(--shadow-color)
    `,
    backdropFilter:
      'blur(calc(var(--blur) * 1px)) saturate(var(--saturate, 2.8)) brightness(var(--brightness, 1.25)) contrast(var(--contrast, 1))',
    borderRadius: 'var(--border-radius)',
    padding: 'calc(var(--padding) * 0.3) var(--padding)',
    border: 'calc(var(--border) * 1px) solid light-dark(oklch(0 0 0 / 0.1), oklch(1 0 0 / 0.2))',
    background:
      'linear-gradient(light-dark(var(--bg-color-first), var(--bg-color-second)) 0 100%) padding-box',
  },
  statsText: {
    display: 'flex',
    gap: '0.25rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    fontSize: (theme: Theme) => theme.fontSizes.smallest,
    lineHeight: 1.5,
    position: 'relative',
    alignItems: 'center',
    fontVariantNumeric: 'tabular-nums',
    textWrap: 'balance',
    whiteSpace: 'wrap',
  },
  spacer: {
    flex: 1,
  },
  actionButton: {
    color: 'oklch(0.7842 0.1265 245)',
    textTransform: 'uppercase',
    fontSize: (theme: Theme) => theme.fontSizes.smallest,
    fontWeight: 600,
    '&:hover': {
      bgcolor: 'oklch(0.7842 0.1265 245 / 0.1)',
    },
  },
  closeIcon: {
    cursor: 'pointer',
    fontSize: '20px',
    '&:hover': {
      opacity: 0.7,
    },
  },
};

const panelVariations = sxVariants({
  base: portfolioBridgePanelStyles.panel,
  variants: {
    theme: {
      dark: {
        '--bg-color-first': 'oklch(0.98 0 0)',
        '--bg-color-second': 'oklch(0.23 0 0)',
        '--text-color': 'oklch(0.26 0 0 / 0.95)',
      },
      satellite: {
        '--bg-color-first': 'oklch(0.98 0 0)',
        '--bg-color-second': 'oklch(0.23 0 0)',
        '--text-color': 'oklch(0.26 0 0 / 0.95)',
      },
      light: {
        '--bg-color-first': 'oklch(0.313 0.0472 275.02)',
        '--bg-color-second': 'oklch(0.98 0 0)',
        '--text-color': 'oklch(0.95 0 0 / 0.95)',
        '--shadow-color': 'oklch(0 0 0 / 0.05)',
      },
    },
    shown: {
      true: {
        translate: '-50% 0 0',
        opacity: 1,
        transition: 'translate 0.2s 0.2s var(--ease-enter), opacity 0.25s 0.2s var(--ease-move)',
      },
      false: {
        translate: '-50% 15% 0',
        opacity: 0,
        transition: 'translate 0.25s 0.2s var(--ease-exit), opacity  0.2s 0.2s var(--ease-move)',
        pointerEvents: 'none',
      },
    },
  },
});

const actionButtonVariations = sxVariants({
  base: portfolioBridgePanelStyles.actionButton,
  variants: {
    theme: {
      dark: {
        color: 'oklch(0.5166 0.16875 245)',
        '&:hover': {
          bgcolor: 'oklch(0.5166 0.16875 245 / 0.1)',
        },
      },
      satellite: {
        color: 'oklch(0.5166 0.16875 245)',
        '&:hover': {
          bgcolor: 'oklch(0.5166 0.16875 245 / 0.1)',
        },
      },
      light: {
        color: 'oklch(0.8491 0.085 245)',
        '&:hover': {
          bgcolor: 'oklch(0.8491 0.085 245 / 0.1)',
        },
      },
    },
  },
});

const badgeStyles = {
  color: 'var(--bg-color-first)',
  bgcolor: 'var(--text-color)',
  borderRadius: '0.25rem',
  padding: '0.075rem 0.25rem',
  fontSize: '0.625rem',
};

export const BRIDGE_ID_URL_SEARCH_PARAM = 'bridgeId';

const Counts = () => {
  const stats = usePortfolioBridgeStore.use.stats();

  return (
    <Box display='flex' gap='0.75rem'>
      {stats.totalAssets > 0 ? (
        <Typography
          variant='body2'
          data-id='portfolio-bridge-stats-text'
          sx={portfolioBridgePanelStyles.statsText}
        >
          <AnimatedCount component='span' sx={badgeStyles} number={stats.totalAssets} />

          {`ASSET${stats.totalAssets > 1 ? 'S' : ''}`}
        </Typography>
      ) : null}

      {stats.totalBlocks > 0 ? (
        <Typography
          variant='body2'
          data-id='portfolio-bridge-stats-text'
          sx={portfolioBridgePanelStyles.statsText}
        >
          <AnimatedCount component='span' sx={badgeStyles} number={stats.totalBlocks} />
          {`BLOCK${stats.totalBlocks > 1 ? 'S' : ''}`}
        </Typography>
      ) : null}
    </Box>
  );
};

const CTA = () => {
  const { entities, filters } = usePortfolioBridgeStore(state => ({
    entities: state.entities,
    filters: state.filters,
  }));

  const theme = usePortfolioBridgeStore.use.theme();

  const { savePortfolioBridge } = useSavePortfolioBridge();

  const handleViewInPA = useCallback(async () => {
    const assetsAndBlocks = entities
      .filter(e => e.type === MaAssetEntityTypeEnum.Asset || e.type === MaAssetEntityTypeEnum.Block)
      .map(entity => ({
        legacyId: entity.legacyId,
        country: {
          __typename: 'Country',
          id: entity.country.id,
          isoCode: entity.country.isoCode,
        },
        displayName: entity.name,
        name: entity.name,
        menuName: entity.name,
        __typename: entity.type === MaAssetEntityTypeEnum.Asset ? 'Asset' : 'Block',
      }));

    const uuid = await savePortfolioBridge({
      assets: assetsAndBlocks,
      countries: filters.countries,
      companies: filters.companies,
      basins: filters.basins,
      shoreStatuses: filters.shoreStatuses,
      developmentStatuses: filters.developmentStatuses,
      npvMin: filters.npvMin,
      npvMax: filters.npvMax,
      productionMin: filters.productionMin,
      productionMax: filters.productionMax,
      reservesMin: filters.reservesMin,
      reservesMax: filters.reservesMax,
      operator: filters.operator,
      opportunities: filters.opportunities,
    });

    if (uuid) {
      window.open(`${PORTFOLIO_ANALYSIS_PATH}?${BRIDGE_ID_URL_SEARCH_PARAM}=${uuid}`, '_blank');
    }
  }, [entities, filters, savePortfolioBridge]);

  return (
    <Button
      variant='text'
      onClick={handleViewInPA}
      data-id='portfolio-bridge-view-pa-button'
      sx={actionButtonVariations({ theme })}
    >
      View in Portfolio Analysis
    </Button>
  );
};

const CloseButton = () => {
  const clearSelection = usePortfolioBridgeStore.use.clearSelection();
  const onClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);
  return (
    <CloseIcon
      onClick={onClose}
      data-id='portfolio-bridge-close-button'
      sx={portfolioBridgePanelStyles.closeIcon}
    />
  );
};

const PortfolioBridgePanelComponent = () => {
  const stats = usePortfolioBridgeStore.use.stats();
  const theme = usePortfolioBridgeStore.use.theme();

  return (
    <Box data-id='portfolio-bridge-panel' sx={panelVariations({ theme, shown: stats.total > 0 })}>
      <Counts />
      <Box sx={portfolioBridgePanelStyles.spacer} />
      <CTA />
      <CloseButton />
    </Box>
  );
};

export const PortfolioBridgePanel = () => {
  const { portfolioBridge } = useFeatureFlags();

  if (!portfolioBridge) return null;

  return createPortal(<PortfolioBridgePanelComponent />, document.body);
};
